import {
  Activity,
  ActivityStandard,
  ActivitySubject,
  FormatValues,
  LearningNeed,
} from "@outschool/business-rules";
import { useTranslation } from "@outschool/localization";
import { searchPath } from "@outschool/routes";
import { useMemo } from "react";

import type { CategoryNavLink, Translate } from "./navbarCategories";

type SearchPathFilters = Record<string, string | number>;

const PRE_K_AND_KINDERGARTEN_GRADE_FILTER = "Pre-Kindergarten,Kindergarten";
const FULL_CURRICULUM_SEARCH_QUERY = "Full Curriculum";
const ACADEMIC_SUBJECT_FILTERS = {
  english: ActivitySubject.English,
  math: ActivitySubject.Math,
  science: ActivitySubject.ScienceAndNature,
  socialStudies: ActivitySubject.SocialStudies,
} as const;
const GRADE_LINKS = [
  ["pre-k-and-kindergarten", PRE_K_AND_KINDERGARTEN_GRADE_FILTER],
  ["1st-grade", "1st grade"],
  ["2nd-grade", "2nd grade"],
  ["3rd-grade", "3rd grade"],
  ["4th-grade", "4th grade"],
  ["5th-grade", "5th grade"],
  ["6th-grade", "6th grade"],
  ["7th-grade", "7th grade"],
  ["8th-grade", "8th grade"],
  ["9th-grade", "9th grade"],
  ["10th-grade", "10th grade"],
  ["11th-grade", "11th grade"],
  ["12th-grade", "12th grade"],
] as const;
const LEARNING_NEED_FILTERS = [
  {
    slug: "neurodiverse",
    label: (t: Translate) => t("Neurodiverse"),
    filters: { q: "neurodiverse" },
  },
  {
    slug: "adhd",
    label: (t: Translate) => t("ADHD"),
    filters: { learningNeeds: LearningNeed.ADHD, q: "ADHD" },
  },
  {
    slug: "autism-spectrum-disorder-asd",
    label: (t: Translate) => t("Autism Spectrum Disorder (ASD)"),
    filters: {
      learningNeeds: LearningNeed.ASD,
      q: "Autism Spectrum Disorder (ASD)",
    },
  },
  {
    slug: "dyslexia-dyscalculia-or-dysgraphia",
    label: (t: Translate) => t("Dyslexia, Dyscalculia, or Dysgraphia"),
    filters: {
      learningNeeds: LearningNeed.DyslexiaDyscalculiaDysgraphia,
      q: "Dyslexia, Dyscalculia, or Dysgraphia",
    },
  },
  {
    slug: "speech-or-language-impairment",
    label: (t: Translate) => t("Speech or Language Impairment"),
    filters: {
      learningNeeds: LearningNeed.SpeechLanguageImpairment,
      q: "Speech or Language Impairment",
    },
  },
  {
    slug: "gifted",
    label: (t: Translate) => t("Gifted"),
    filters: { q: "gifted" },
  },
] as const;

function searchCategoryPath(filters: SearchPathFilters): string {
  return searchPath(filters);
}

function cloneWithSearchPaths(
  category: CategoryNavLink,
  defaultFilters: SearchPathFilters = {},
): CategoryNavLink {
  return {
    ...category,
    path:
      category.path ??
      searchCategoryPath({
        ...defaultFilters,
        q: category.slug.replace(/-/g, " "),
      }),
    getSubcategories: category.getSubcategories
      ? () =>
          category.getSubcategories!().map(subcategory =>
            cloneWithSearchPaths(subcategory, defaultFilters),
          )
      : undefined,
  };
}

function gradeNavLinks({
  t,
  slugSuffix,
  getFilters,
}: {
  t: Translate;
  slugSuffix: string;
  getFilters: (gradeLevel: string) => SearchPathFilters;
}): CategoryNavLink[] {
  const gradeLabels = [
    t("Pre-K & Kindergarten"),
    t("1st Grade"),
    t("2nd Grade"),
    t("3rd Grade"),
    t("4th Grade"),
    t("5th Grade"),
    t("6th Grade"),
    t("7th Grade"),
    t("8th Grade"),
    t("9th Grade"),
    t("10th Grade"),
    t("11th Grade"),
    t("12th Grade"),
  ];
  return GRADE_LINKS.map(([slug, gradeLevel], index) => ({
    nameTitleCased: gradeLabels[index],
    slug: `${slug}-${slugSuffix}`,
    path: searchCategoryPath(getFilters(gradeLevel)),
  }));
}

function learningNeedsNavLinks({
  t,
  extraFilters = {},
}: {
  t: Translate;
  extraFilters?: SearchPathFilters;
}): CategoryNavLink[] {
  return LEARNING_NEED_FILTERS.map(({ slug, label, filters }) => ({
    nameTitleCased: label(t),
    slug,
    path: searchCategoryPath({ ...extraFilters, ...filters }),
  }));
}

export function useBackToSchoolNavbarCategories({
  electivesCategory,
  justForFunCategory,
}: {
  electivesCategory: CategoryNavLink;
  justForFunCategory: CategoryNavLink;
}): CategoryNavLink[] {
  const { t } = useTranslation(
    "client\\components\\nav\\CategoryNavbar\\BackToSchoolNavbarCategories",
  );

  return useMemo(
    () =>
      buildBackToSchoolNavbarCategories({
        electivesCategory,
        justForFunCategory,
        t,
      }),
    [electivesCategory, justForFunCategory, t],
  );
}

function buildBackToSchoolNavbarCategories({
  electivesCategory,
  justForFunCategory,
  t,
}: {
  electivesCategory: CategoryNavLink;
  justForFunCategory: CategoryNavLink;
  t: Translate;
}): CategoryNavLink[] {
  const tutoringFilters = {
    capacityMax: 1,
    format: FormatValues.Live,
  };
  const fullCourseFilters = {
    capacityMin: 2,
    delivery: Activity.DeliveryTypeValues.Course,
  };
  const fullCurriculumFilters = {
    ...fullCourseFilters,
    q: FULL_CURRICULUM_SEARCH_QUERY,
  };
  // No delivery=Course: skips the strict academic-curriculum query. capacityMin excludes 1-on-1.
  const learningMethodFilters = {
    capacityMin: 2,
  };

  const academicCurriculumCategory: CategoryNavLink = {
    nameTitleCased: t("Academic Curriculum"),
    slug: "academic-curriculum",
    showMore: true,
    can_link: true,
    path: searchCategoryPath(fullCourseFilters),
    getSubcategories: () => [
      {
        nameTitleCased: t("All-subject"),
        slug: "full-curriculum",
        path: searchCategoryPath(fullCurriculumFilters),
        getSubcategories: () =>
          gradeNavLinks({
            t,
            slugSuffix: "full-curriculum",
            getFilters: gradeLevel => ({
              ...fullCurriculumFilters,
              gradeLevel,
            }),
          }),
      },
      {
        nameTitleCased: t("English"),
        slug: "english",
        path: searchCategoryPath({
          ...fullCourseFilters,
          theme: ACADEMIC_SUBJECT_FILTERS.english,
        }),
        getSubcategories: () =>
          gradeNavLinks({
            t,
            slugSuffix: "english",
            getFilters: gradeLevel => ({
              ...fullCourseFilters,
              theme: ACADEMIC_SUBJECT_FILTERS.english,
              gradeLevel,
            }),
          }),
      },
      {
        nameTitleCased: t("Math"),
        slug: "math",
        path: searchCategoryPath({
          ...fullCourseFilters,
          theme: ACADEMIC_SUBJECT_FILTERS.math,
        }),
        getSubcategories: () =>
          gradeNavLinks({
            t,
            slugSuffix: "math",
            getFilters: gradeLevel => ({
              ...fullCourseFilters,
              theme: ACADEMIC_SUBJECT_FILTERS.math,
              gradeLevel,
            }),
          }),
      },
      {
        nameTitleCased: t("Science"),
        slug: "science",
        path: searchCategoryPath({
          ...fullCourseFilters,
          theme: ACADEMIC_SUBJECT_FILTERS.science,
        }),
        getSubcategories: () =>
          gradeNavLinks({
            t,
            slugSuffix: "science",
            getFilters: gradeLevel => ({
              ...fullCourseFilters,
              theme: ACADEMIC_SUBJECT_FILTERS.science,
              gradeLevel,
            }),
          }),
      },
      {
        nameTitleCased: t("Social Studies"),
        slug: "social-studies",
        path: searchCategoryPath({
          ...fullCourseFilters,
          theme: ACADEMIC_SUBJECT_FILTERS.socialStudies,
        }),
        getSubcategories: () =>
          gradeNavLinks({
            t,
            slugSuffix: "social-studies",
            getFilters: gradeLevel => ({
              ...fullCourseFilters,
              theme: ACADEMIC_SUBJECT_FILTERS.socialStudies,
              gradeLevel,
            }),
          }),
      },
      {
        nameTitleCased: t("Learning Methods"),
        slug: "learning-methods",
        path: searchCategoryPath({
          ...learningMethodFilters,
          q: "learning methods",
        }),
        getSubcategories: () => [
          {
            nameTitleCased: t("Project-Based"),
            slug: "project-based",
            path: searchCategoryPath({
              ...learningMethodFilters,
              q: "project based",
            }),
          },
          {
            nameTitleCased: t("Montessori"),
            slug: "montessori",
            path: searchCategoryPath({
              ...learningMethodFilters,
              q: "montessori",
            }),
          },
          {
            nameTitleCased: t("Waldorf"),
            slug: "waldorf",
            path: searchCategoryPath({
              ...learningMethodFilters,
              q: "waldorf",
            }),
          },
          {
            nameTitleCased: t("Common Core (CCSS)"),
            slug: "common-core-ccss",
            path: searchCategoryPath({
              ...learningMethodFilters,
              q: "Common Core (CCSS)",
              standards: ActivityStandard.CCSS,
            }),
          },
          {
            nameTitleCased: t("Next Gen Science (NGSS)"),
            slug: "next-gen-science-ngss",
            path: searchCategoryPath({
              ...learningMethodFilters,
              q: "Next Gen Science (NGSS)",
              standards: ActivityStandard.NGSS,
            }),
          },
        ],
      },
    ],
  };

  const tutoringCategory: CategoryNavLink = {
    nameTitleCased: t("Tutoring"),
    slug: "tutoring",
    showMore: true,
    can_link: true,
    path: searchCategoryPath(tutoringFilters),
    getSubcategories: () => [
      {
        nameTitleCased: t("English"),
        slug: "english-tutoring",
        path: searchCategoryPath({
          ...tutoringFilters,
          theme: ACADEMIC_SUBJECT_FILTERS.english,
        }),
        getSubcategories: () =>
          gradeNavLinks({
            t,
            slugSuffix: "english-tutoring",
            getFilters: gradeLevel => ({
              ...tutoringFilters,
              theme: ACADEMIC_SUBJECT_FILTERS.english,
              gradeLevel,
            }),
          }),
      },
      {
        nameTitleCased: t("Math"),
        slug: "math-tutoring",
        path: searchCategoryPath({
          ...tutoringFilters,
          theme: ACADEMIC_SUBJECT_FILTERS.math,
        }),
        getSubcategories: () =>
          gradeNavLinks({
            t,
            slugSuffix: "math-tutoring",
            getFilters: gradeLevel => ({
              ...tutoringFilters,
              theme: ACADEMIC_SUBJECT_FILTERS.math,
              gradeLevel,
            }),
          }),
      },
      {
        nameTitleCased: t("Science"),
        slug: "science-tutoring",
        path: searchCategoryPath({
          ...tutoringFilters,
          theme: ACADEMIC_SUBJECT_FILTERS.science,
        }),
        getSubcategories: () =>
          gradeNavLinks({
            t,
            slugSuffix: "science-tutoring",
            getFilters: gradeLevel => ({
              ...tutoringFilters,
              theme: ACADEMIC_SUBJECT_FILTERS.science,
              gradeLevel,
            }),
          }),
      },
      {
        nameTitleCased: t("Social Studies"),
        slug: "social-studies-tutoring",
        path: searchCategoryPath({
          ...tutoringFilters,
          theme: ACADEMIC_SUBJECT_FILTERS.socialStudies,
        }),
        getSubcategories: () =>
          gradeNavLinks({
            t,
            slugSuffix: "social-studies-tutoring",
            getFilters: gradeLevel => ({
              ...tutoringFilters,
              theme: ACADEMIC_SUBJECT_FILTERS.socialStudies,
              gradeLevel,
            }),
          }),
      },
      {
        nameTitleCased: t("Private Lessons"),
        slug: "private-lessons-tutoring",
        path: searchCategoryPath({ ...tutoringFilters, q: "private lessons" }),
        getSubcategories: () => [
          {
            nameTitleCased: t("Acting & Improv"),
            slug: "acting-and-improv-lessons",
            path: searchCategoryPath({
              ...tutoringFilters,
              q: "acting improv",
            }),
          },
          {
            nameTitleCased: t("Arts"),
            slug: "arts-lessons",
            path: searchCategoryPath({
              ...tutoringFilters,
              theme: ActivitySubject.Arts,
            }),
          },
          {
            nameTitleCased: t("Chess"),
            slug: "chess-lessons",
            path: searchCategoryPath({ ...tutoringFilters, q: "chess" }),
          },
          {
            nameTitleCased: t("Coding"),
            slug: "coding-lessons",
            path: searchCategoryPath({
              ...tutoringFilters,
              theme: ActivitySubject.CodingAndTech,
            }),
          },
          {
            nameTitleCased: t("Dance"),
            slug: "dance-lessons",
            path: searchCategoryPath({ ...tutoringFilters, q: "dance" }),
          },
          {
            nameTitleCased: t("Geometry"),
            slug: "geometry-lessons",
            path: searchCategoryPath({ ...tutoringFilters, q: "geometry" }),
          },
          {
            nameTitleCased: t("Music"),
            slug: "music-lessons",
            path: searchCategoryPath({
              ...tutoringFilters,
              theme: ActivitySubject.Music,
            }),
          },
          {
            nameTitleCased: t("Photography"),
            slug: "photography-lessons",
            path: searchCategoryPath({ ...tutoringFilters, q: "photography" }),
          },
          {
            nameTitleCased: t("Public Speaking"),
            slug: "public-speaking-lessons",
            path: searchCategoryPath({
              ...tutoringFilters,
              q: "public speaking",
            }),
          },
          {
            nameTitleCased: t("Reading"),
            slug: "reading-lessons",
            path: searchCategoryPath({ ...tutoringFilters, q: "reading" }),
          },
          {
            nameTitleCased: t("Writing"),
            slug: "writing-lessons",
            path: searchCategoryPath({ ...tutoringFilters, q: "writing" }),
          },
        ],
      },
      {
        nameTitleCased: t("1-on-1 Coaching"),
        slug: "private-coaching-tutoring",
        path: searchCategoryPath({ ...tutoringFilters, q: "coaching" }),
        getSubcategories: () => [
          {
            nameTitleCased: t("Executive Function"),
            slug: "executive-function-coaching",
            path: searchCategoryPath({
              ...tutoringFilters,
              q: "executive function",
            }),
          },
          {
            nameTitleCased: t("Study Skills"),
            slug: "study-skills-coaching",
            path: searchCategoryPath({ ...tutoringFilters, q: "study skills" }),
          },
          {
            nameTitleCased: t("Social Skills"),
            slug: "social-skills-coaching",
            path: searchCategoryPath({
              ...tutoringFilters,
              q: "social skills",
            }),
          },
          {
            nameTitleCased: t("Phonics & Early Reading"),
            slug: "phonics-and-early-reading-coaching",
            path: searchCategoryPath({
              ...tutoringFilters,
              q: "phonics early reading",
            }),
          },
        ],
      },
      {
        nameTitleCased: t("World Languages"),
        slug: "languages-tutoring",
        path: searchCategoryPath({
          ...tutoringFilters,
          theme: ActivitySubject.WorldLanguages,
        }),
        getSubcategories: () => [
          {
            nameTitleCased: t("Spanish"),
            slug: "spanish-tutoring",
            path: searchCategoryPath({ ...tutoringFilters, q: "spanish" }),
          },
          {
            nameTitleCased: t("French"),
            slug: "french-tutoring",
            path: searchCategoryPath({ ...tutoringFilters, q: "french" }),
          },
          {
            nameTitleCased: t("Korean"),
            slug: "korean-tutoring",
            path: searchCategoryPath({ ...tutoringFilters, q: "korean" }),
          },
          {
            nameTitleCased: t("All"),
            slug: "all-languages-tutoring",
            path: searchCategoryPath({
              ...tutoringFilters,
              theme: ActivitySubject.WorldLanguages,
            }),
          },
        ],
      },
      {
        nameTitleCased: t("Test Prep"),
        slug: "test-prep-tutoring",
        path: searchCategoryPath({
          ...tutoringFilters,
          theme: ActivitySubject.TestPrep,
        }),
        getSubcategories: () => [
          {
            nameTitleCased: t("State Test Prep"),
            slug: "state-test-prep-tutoring",
            path: searchCategoryPath({
              ...tutoringFilters,
              q: "State",
              theme: ActivitySubject.TestPrep,
            }),
          },
          {
            nameTitleCased: t("AP Test Prep"),
            slug: "ap-test-prep-tutoring",
            path: searchCategoryPath({
              ...tutoringFilters,
              q: "AP",
              theme: ActivitySubject.TestPrep,
            }),
          },
          {
            nameTitleCased: t("SAT/ACT"),
            slug: "sat-act-tutoring",
            path: searchCategoryPath({
              ...tutoringFilters,
              q: "SAT ACT",
              theme: ActivitySubject.TestPrep,
            }),
          },
          {
            nameTitleCased: t("All"),
            slug: "all-test-prep-tutoring",
            path: searchCategoryPath({
              ...tutoringFilters,
              theme: ActivitySubject.TestPrep,
            }),
          },
        ],
      },
    ],
  };

  const electivesAndEnrichmentCategory = cloneWithSearchPaths(
    {
      ...electivesCategory,
      nameTitleCased: t("Electives & Enrichment"),
      slug: "electives-and-enrichment",
      path: searchCategoryPath({
        delivery: Activity.DeliveryTypeValues.EnrichmentOrClub,
        q: "electives enrichment",
      }),
    },
    {
      delivery: Activity.DeliveryTypeValues.EnrichmentOrClub,
    },
  );

  const socialClubsCategory = cloneWithSearchPaths({
    ...justForFunCategory,
    nameTitleCased: t("Social Clubs"),
    slug: "social-clubs",
    path: searchCategoryPath({ q: "social clubs" }),
  });
  const learningNeedsCategory: CategoryNavLink = {
    nameTitleCased: t("Learning Needs"),
    slug: "learning-needs",
    showMore: true,
    can_link: true,
    renderAsFlatList: true,
    path: searchCategoryPath({ q: "learning needs" }),
    getSubcategories: () => learningNeedsNavLinks({ t }),
  };

  return [
    academicCurriculumCategory,
    tutoringCategory,
    electivesAndEnrichmentCategory,
    socialClubsCategory,
    learningNeedsCategory,
  ];
}
